import type { ReactionType, FeedbackType } from '@/types'
import {
  DEMO_ARTIST,
  DEMO_EVENT,
  DEMO_SONGS,
  DEMO_SETLISTS,
  getRandomSongNotInQueue,
  createDemoArtist,
  createDemoEvent,
  createDemoSetlists,
  generateTranslatedEventTitle,
  type DemoSong,
  type DemoSetlist,
  type DemoEventSetlist,
} from './demo-data'
import type {
  DemoState,
  DemoQueueRequest,
  DemoNowPlaying,
  DemoPlayedSong,
  DemoEventSettings,
  DemoReaction,
  DemoFeedback,
  DemoTimer,
  DemoNote,
  DemoSquelchedSong,
} from './demo-types'

// ─── Utilities ───────────────────────────────────────────────────────────

let idCounter = 1000
export function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${idCounter++}`
}

export const FAKE_NAMES = [
  'Alex', 'Jordan', 'Taylor', 'Morgan', 'Casey', 'Riley', 'Quinn', 'Avery',
  'Drew', 'Sam', 'Jamie', 'Chris', 'Pat', 'Dana', 'Robin', 'Skyler',
  'Blake', 'Reese', 'Cameron', 'Parker', 'Jesse', 'Sage', 'River', 'Phoenix',
]

export function getRandomName(): string {
  return FAKE_NAMES[Math.floor(Math.random() * FAKE_NAMES.length)]
}

function sortQueue(queue: DemoQueueRequest[]): DemoQueueRequest[] {
  return [...queue].sort((a, b) => {
    if (b.totalTipsCents !== a.totalTipsCents) return b.totalTipsCents - a.totalTipsCents
    if (b.upvotes !== a.upvotes) return b.upvotes - a.upvotes
    return a.createdAt.getTime() - b.createdAt.getTime()
  })
}

// ─── Default settings ────────────────────────────────────────────────────

const DEFAULT_SETTINGS: DemoEventSettings = {
  requestsEnabled: true,
  tipsEnabled: true,
  reactionsEnabled: true,
  feedbackEnabled: true,
  showQueue: true,
  showNowPlaying: true,
  tipAmounts: [200, 500, 1000, 2000],
  songRequestFee: 0,
  maxQueueSize: 50,
}

// ─── Initial state ───────────────────────────────────────────────────────

/** Translated mock data strings passed from the DemoProvider */
export interface DemoTranslations {
  bio: string
  venue: string
  eventDay: string
  eventSlot: string
  setlistClassicRock: string
  setlistSaturdayNight: string
}

export function createInitialState(translations?: DemoTranslations): DemoState {
  const artist = translations
    ? createDemoArtist(translations.bio)
    : DEMO_ARTIST
  const event = translations
    ? createDemoEvent(
        generateTranslatedEventTitle(translations.eventDay, translations.eventSlot),
        translations.venue,
      )
    : DEMO_EVENT
  const setlists: DemoSetlist[] = translations
    ? createDemoSetlists({
        classicRock: translations.setlistClassicRock,
        saturdayNight: translations.setlistSaturdayNight,
      })
    : DEMO_SETLISTS

  return {
    phase: 'pre-event',
    artist,
    event,
    songs: DEMO_SONGS,
    queue: [],
    nowPlaying: null,
    lastPlayed: null,
    playedSongs: [],
    visitorCount: 3,
    totalEarningsCents: 0,
    recentReactions: [],
    recentFeedback: [],
    breakStatus: { onBreak: false, startedAt: null, expectedReturnAt: null },
    settings: DEFAULT_SETTINGS,
    timers: [],
    notes: [],
    squelchedSongs: [],
    visitorUpvotedRequestIds: new Set(),
    visitorRequestCount: 0,
    visitorHasTipped: false,
    visitorReactionTokens: 3,
    setlists,
    activeSetlist: null,
    currentTime: Date.now(),
  }
}

// ─── Actions ─────────────────────────────────────────────────────────────

export type DemoAction =
  // Lifecycle
  | { type: 'GO_LIVE' }
  | { type: 'TICK' }

  // Audience actions
  | { type: 'REQUEST_SONG'; songId: string; tipCents: number; isFromVisitor: boolean; visitorName?: string }
  | { type: 'UPVOTE_SONG'; requestId: string; isFromVisitor: boolean }
  | { type: 'ADD_TIP_TO_REQUEST'; requestId: string; tipCents: number; isFromVisitor: boolean }
  | { type: 'CANCEL_BOOST'; requestId: string }
  | { type: 'SEND_DIRECT_TIP'; tipCents: number; isFromVisitor: boolean }
  | { type: 'SEND_REACTION'; reactionType: ReactionType }
  | { type: 'SEND_FEEDBACK'; feedbackType: FeedbackType; message?: string }

  // Artist actions
  | { type: 'PLAY_SONG'; songId: string }
  | { type: 'PLAY_FROM_QUEUE'; requestId: string }
  | { type: 'END_CURRENT_SONG' }
  | { type: 'SKIP_REQUEST'; requestId: string }
  | { type: 'SQUELCH_SONG'; songId: string; durationMinutes: number }
  | { type: 'UNSQUELCH_SONG'; songId: string }
  | { type: 'TOGGLE_SETTING'; key: keyof DemoEventSettings }
  | { type: 'UPDATE_TIP_AMOUNTS'; amounts: number[] }
  | { type: 'START_BREAK'; durationMinutes: number }
  | { type: 'END_BREAK' }

  // Timer actions
  | { type: 'START_TIMER'; timerType: DemoTimer['type']; durationMinutes: number; label: string; isPublic: boolean }
  | { type: 'CANCEL_TIMER'; timerId: string }
  | { type: 'EXTEND_TIMER'; timerId: string; additionalMinutes: number }
  | { type: 'EDIT_TIMER'; timerId: string; updates: { label?: string; isPublic?: boolean } }

  // Note actions
  | { type: 'CREATE_NOTE'; content: string; category: DemoNote['category']; linkToCurrentSong: boolean }
  | { type: 'UPDATE_NOTE'; noteId: string; content: string }
  | { type: 'DELETE_NOTE'; noteId: string }

  // Setlist actions
  | { type: 'ACTIVATE_SETLIST'; setlistId: string }
  | { type: 'CLEAR_SETLIST' }
  | { type: 'PLAY_FROM_SETLIST'; position: number }

  // Simulation controls
  | { type: 'SET_VISITOR_COUNT'; count: number }
  | { type: 'ADD_SIMULATED_REQUEST' }
  | { type: 'ADD_SIMULATED_UPVOTE' }
  | { type: 'ADD_SIMULATED_BOOST' }
  | { type: 'ADD_SIMULATED_TIP' }
  | { type: 'ADD_SIMULATED_REACTION' }
  | { type: 'ADD_SIMULATED_FEEDBACK' }

  // Reaction token regeneration
  | { type: 'REGENERATE_REACTION_TOKEN' }

// ─── Helpers for play song logic ─────────────────────────────────────────

function finishCurrentSong(state: DemoState): Pick<DemoState, 'lastPlayed' | 'playedSongs' | 'activeSetlist'> {
  const result: Pick<DemoState, 'lastPlayed' | 'playedSongs' | 'activeSetlist'> = {
    lastPlayed: state.lastPlayed,
    playedSongs: state.playedSongs,
    activeSetlist: state.activeSetlist,
  }

  if (state.nowPlaying) {
    const played: DemoPlayedSong = {
      id: generateId('played'),
      song: state.nowPlaying.song,
      playedAt: state.nowPlaying.startedAt,
      wasRequested: state.nowPlaying.wasRequested,
      tipsCents: state.nowPlaying.capturedTipsCents,
    }
    result.lastPlayed = played
    result.playedSongs = [played, ...state.playedSongs]

    // Update setlist if applicable
    if (state.activeSetlist) {
      const setlistSongIndex = state.activeSetlist.allSongs.findIndex(s => s.songId === state.nowPlaying!.song.id)
      if (setlistSongIndex !== -1) {
        const updatedSongs = state.activeSetlist.allSongs.map((s, i) => ({
          ...s,
          isPlayed: i === setlistSongIndex ? true : s.isPlayed,
          isCurrentlyPlaying: false,
        }))
        const playedCount = updatedSongs.filter(s => s.isPlayed).length
        const remainingSongs = updatedSongs.filter(s => !s.isPlayed)
        const remainingDuration = remainingSongs.reduce((sum, s) => sum + s.song.durationMs, 0)
        const nextUnplayedIndex = updatedSongs.findIndex(s => !s.isPlayed)
        if (nextUnplayedIndex >= 0) updatedSongs[nextUnplayedIndex].isNext = true

        result.activeSetlist = {
          ...state.activeSetlist,
          allSongs: updatedSongs,
          songsPlayed: playedCount,
          remainingDurationMs: remainingDuration,
          isComplete: playedCount >= state.activeSetlist.totalSongs,
          currentPosition: nextUnplayedIndex >= 0 ? updatedSongs[nextUnplayedIndex].position : state.activeSetlist.totalSongs,
        }
      }
    }
  }
  return result
}

function startPlayingSong(state: DemoState, song: DemoSong, queueRequest?: DemoQueueRequest): Partial<DemoState> {
  const finished = finishCurrentSong(state)

  const newNowPlaying: DemoNowPlaying = {
    id: generateId('np'),
    song,
    startedAt: new Date(),
    wasRequested: !!queueRequest,
    capturedTipsCents: queueRequest?.totalTipsCents || 0,
    requestId: queueRequest?.id,
  }

  let newQueue = state.queue
  let newEarnings = state.totalEarningsCents
  if (queueRequest?.totalTipsCents) {
    newEarnings += queueRequest.totalTipsCents
  }
  if (queueRequest) {
    newQueue = state.queue.filter(r => r.id !== queueRequest.id)
  }

  // Update setlist for new song
  let activeSetlist = finished.activeSetlist
  if (activeSetlist) {
    const setlistSongIndex = activeSetlist.allSongs.findIndex(s => s.songId === song.id)
    if (setlistSongIndex !== -1) {
      const updatedSongs = activeSetlist.allSongs.map((s, i) => ({
        ...s,
        isCurrentlyPlaying: i === setlistSongIndex,
        isNext: false,
      }))
      const nextUnplayedIndex = updatedSongs.findIndex((s, i) => !s.isPlayed && i !== setlistSongIndex)
      if (nextUnplayedIndex >= 0) updatedSongs[nextUnplayedIndex].isNext = true
      activeSetlist = { ...activeSetlist, allSongs: updatedSongs }
    }
  }

  return {
    nowPlaying: newNowPlaying,
    queue: sortQueue(newQueue),
    totalEarningsCents: newEarnings,
    lastPlayed: finished.lastPlayed,
    playedSongs: finished.playedSongs,
    activeSetlist,
    breakStatus: state.breakStatus.onBreak
      ? { onBreak: false, startedAt: null, expectedReturnAt: null }
      : state.breakStatus,
  }
}

// ─── Reducer ─────────────────────────────────────────────────────────────

export function demoReducer(state: DemoState, action: DemoAction): DemoState {
  switch (action.type) {
    // ── Lifecycle ──────────────────────────────────────────────────────
    case 'GO_LIVE': {
      return {
        ...state,
        phase: 'live',
        queue: [], // Queue starts empty — requests trickle in via simulation
        visitorCount: state.visitorCount + 18, // existing waiters + burst of new arrivals
        totalEarningsCents: 1250,
        event: { ...state.event, status: 'live', startTime: new Date() },
      }
    }

    case 'TICK': {
      return { ...state, currentTime: Date.now() }
    }

    // ── Audience actions ──────────────────────────────────────────────
    case 'REQUEST_SONG': {
      const song = DEMO_SONGS.find(s => s.id === action.songId)
      if (!song) return state

      const existingRequest = state.queue.find(r => r.songId === action.songId)
      if (existingRequest) {
        if (action.tipCents > 0) {
          const updatedQueue = state.queue.map(r =>
            r.id === existingRequest.id
              ? {
                  ...r,
                  totalTipsCents: r.totalTipsCents + action.tipCents,
                  visitorBoostCents: action.isFromVisitor ? r.visitorBoostCents + action.tipCents : r.visitorBoostCents,
                }
              : r
          )
          return {
            ...state,
            queue: sortQueue(updatedQueue),
            visitorHasTipped: action.isFromVisitor ? true : state.visitorHasTipped,
            visitorReactionTokens: action.isFromVisitor && action.tipCents > 0 ? 10 : state.visitorReactionTokens,
          }
        }
        return state
      }

      const newRequest: DemoQueueRequest = {
        id: generateId('req'),
        songId: action.songId,
        song,
        upvotes: 0,
        totalTipsCents: action.tipCents,
        visitorBoostCents: action.isFromVisitor ? action.tipCents : 0,
        createdAt: new Date(),
        isFromVisitor: action.isFromVisitor,
        visitorName: action.visitorName || (action.isFromVisitor ? 'You' : getRandomName()),
      }

      return {
        ...state,
        queue: sortQueue([...state.queue, newRequest]),
        visitorRequestCount: action.isFromVisitor ? state.visitorRequestCount + 1 : state.visitorRequestCount,
        visitorHasTipped: action.isFromVisitor && action.tipCents > 0 ? true : state.visitorHasTipped,
        visitorReactionTokens: action.isFromVisitor && action.tipCents > 0 ? 10 : state.visitorReactionTokens,
      }
    }

    case 'UPVOTE_SONG': {
      if (action.isFromVisitor && state.visitorUpvotedRequestIds.has(action.requestId)) return state
      const updatedQueue = state.queue.map(r =>
        r.id === action.requestId ? { ...r, upvotes: r.upvotes + 1 } : r
      )
      return {
        ...state,
        queue: sortQueue(updatedQueue),
        visitorUpvotedRequestIds: action.isFromVisitor
          ? new Set([...state.visitorUpvotedRequestIds, action.requestId])
          : state.visitorUpvotedRequestIds,
      }
    }

    case 'ADD_TIP_TO_REQUEST': {
      const updatedQueue = state.queue.map(r =>
        r.id === action.requestId
          ? {
              ...r,
              totalTipsCents: r.totalTipsCents + action.tipCents,
              visitorBoostCents: action.isFromVisitor ? r.visitorBoostCents + action.tipCents : r.visitorBoostCents,
            }
          : r
      )
      return {
        ...state,
        queue: sortQueue(updatedQueue),
        visitorHasTipped: action.isFromVisitor && action.tipCents > 0 ? true : state.visitorHasTipped,
        visitorReactionTokens: action.isFromVisitor && action.tipCents > 0 ? 10 : state.visitorReactionTokens,
      }
    }

    case 'CANCEL_BOOST': {
      const updatedQueue = state.queue.map(r =>
        r.id === action.requestId
          ? { ...r, totalTipsCents: r.totalTipsCents - r.visitorBoostCents, visitorBoostCents: 0 }
          : r
      )
      return { ...state, queue: sortQueue(updatedQueue) }
    }

    case 'SEND_DIRECT_TIP': {
      return {
        ...state,
        totalEarningsCents: state.totalEarningsCents + action.tipCents,
        visitorHasTipped: action.isFromVisitor && action.tipCents > 0 ? true : state.visitorHasTipped,
        visitorReactionTokens: action.isFromVisitor && action.tipCents > 0 ? 10 : state.visitorReactionTokens,
      }
    }

    case 'SEND_REACTION': {
      if (state.visitorReactionTokens <= 0) return state
      const reaction: DemoReaction = {
        id: generateId('reaction'),
        type: action.reactionType,
        timestamp: new Date(),
      }
      return {
        ...state,
        visitorReactionTokens: Math.max(0, state.visitorReactionTokens - 1),
        recentReactions: [...state.recentReactions.slice(-49), reaction],
      }
    }

    case 'SEND_FEEDBACK': {
      const feedback: DemoFeedback = {
        id: generateId('feedback'),
        type: action.feedbackType,
        timestamp: new Date(),
        message: action.message,
      }
      return {
        ...state,
        recentFeedback: [...state.recentFeedback.slice(-19), feedback],
      }
    }

    // ── Artist actions ────────────────────────────────────────────────
    case 'PLAY_SONG': {
      const song = DEMO_SONGS.find(s => s.id === action.songId)
      if (!song) return state
      const queueRequest = state.queue.find(r => r.songId === action.songId)
      return { ...state, ...startPlayingSong(state, song, queueRequest) }
    }

    case 'PLAY_FROM_QUEUE': {
      const request = state.queue.find(r => r.id === action.requestId)
      if (!request) return state
      return { ...state, ...startPlayingSong(state, request.song, request) }
    }

    case 'END_CURRENT_SONG': {
      if (!state.nowPlaying) return state
      const finished = finishCurrentSong(state)
      return { ...state, nowPlaying: null, ...finished }
    }

    case 'SKIP_REQUEST': {
      return { ...state, queue: state.queue.filter(r => r.id !== action.requestId) }
    }

    case 'SQUELCH_SONG': {
      const until = new Date(Date.now() + action.durationMinutes * 60 * 1000)
      const existing = state.squelchedSongs.find(s => s.songId === action.songId)
      const squelchedSongs: DemoSquelchedSong[] = existing
        ? state.squelchedSongs.map(s => s.songId === action.songId ? { ...s, until } : s)
        : [...state.squelchedSongs, { songId: action.songId, until }]
      return {
        ...state,
        squelchedSongs,
        queue: state.queue.filter(r => r.songId !== action.songId),
      }
    }

    case 'UNSQUELCH_SONG': {
      return { ...state, squelchedSongs: state.squelchedSongs.filter(s => s.songId !== action.songId) }
    }

    case 'TOGGLE_SETTING': {
      const value = state.settings[action.key]
      if (typeof value !== 'boolean') return state
      return { ...state, settings: { ...state.settings, [action.key]: !value } }
    }

    case 'UPDATE_TIP_AMOUNTS': {
      return { ...state, settings: { ...state.settings, tipAmounts: action.amounts } }
    }

    case 'START_BREAK': {
      let newState = state
      if (state.nowPlaying) {
        const finished = finishCurrentSong(state)
        newState = { ...state, nowPlaying: null, ...finished }
      }
      return {
        ...newState,
        breakStatus: {
          onBreak: true,
          startedAt: new Date(),
          expectedReturnAt: new Date(Date.now() + action.durationMinutes * 60 * 1000),
        },
      }
    }

    case 'END_BREAK': {
      return { ...state, breakStatus: { onBreak: false, startedAt: null, expectedReturnAt: null } }
    }

    // ── Timer actions ─────────────────────────────────────────────────
    case 'START_TIMER': {
      const timer: DemoTimer = {
        id: generateId('timer'),
        type: action.timerType,
        label: action.label || action.timerType,
        endsAt: new Date(Date.now() + action.durationMinutes * 60 * 1000),
        isPublic: action.isPublic,
        createdAt: new Date(),
      }
      return { ...state, timers: [...state.timers, timer] }
    }

    case 'CANCEL_TIMER': {
      return { ...state, timers: state.timers.filter(t => t.id !== action.timerId) }
    }

    case 'EXTEND_TIMER': {
      return {
        ...state,
        timers: state.timers.map(t =>
          t.id === action.timerId
            ? { ...t, endsAt: new Date(t.endsAt.getTime() + action.additionalMinutes * 60 * 1000) }
            : t
        ),
      }
    }

    case 'EDIT_TIMER': {
      return {
        ...state,
        timers: state.timers.map(t =>
          t.id === action.timerId ? { ...t, ...action.updates } : t
        ),
      }
    }

    // ── Note actions ──────────────────────────────────────────────────
    case 'CREATE_NOTE': {
      const note: DemoNote = {
        id: generateId('note'),
        content: action.content,
        category: action.category,
        createdAt: new Date(),
        ...(action.linkToCurrentSong && state.nowPlaying
          ? { songId: state.nowPlaying.song.id, songTitle: state.nowPlaying.song.title }
          : {}),
      }
      return { ...state, notes: [...state.notes, note] }
    }

    case 'UPDATE_NOTE': {
      return {
        ...state,
        notes: state.notes.map(n => n.id === action.noteId ? { ...n, content: action.content } : n),
      }
    }

    case 'DELETE_NOTE': {
      return { ...state, notes: state.notes.filter(n => n.id !== action.noteId) }
    }

    // ── Setlist actions ───────────────────────────────────────────────
    case 'ACTIVATE_SETLIST': {
      const setlist = state.setlists.find(s => s.id === action.setlistId)
      if (!setlist) return state

      const eventSetlist: DemoEventSetlist = {
        setlistId: setlist.id,
        setlist,
        currentPosition: 0,
        allSongs: setlist.songs.map((s, i) => ({
          position: s.position,
          songId: s.songId,
          song: s.song,
          isPlayed: false,
          isCurrentlyPlaying: false,
          isNext: i === 0,
        })),
        totalSongs: setlist.songs.length,
        songsPlayed: 0,
        remainingDurationMs: setlist.totalDurationMs,
        isComplete: false,
      }
      return { ...state, activeSetlist: eventSetlist }
    }

    case 'CLEAR_SETLIST': {
      return { ...state, activeSetlist: null }
    }

    case 'PLAY_FROM_SETLIST': {
      if (!state.activeSetlist) return state
      const setlistSong = state.activeSetlist.allSongs.find(s => s.position === action.position)
      if (!setlistSong) return state
      return { ...state, ...startPlayingSong(state, setlistSong.song) }
    }

    // ── Simulation controls ───────────────────────────────────────────
    case 'SET_VISITOR_COUNT': {
      return { ...state, visitorCount: action.count }
    }

    case 'ADD_SIMULATED_REQUEST': {
      const queueSongIds = state.queue.map(r => r.songId)
      const song = getRandomSongNotInQueue(queueSongIds)
      if (!song || state.squelchedSongs.some(s => s.songId === song.id && s.until.getTime() > Date.now())) {
        return state
      }
      const newRequest: DemoQueueRequest = {
        id: generateId('req'),
        songId: song.id,
        song,
        upvotes: 0,
        totalTipsCents: 0,
        visitorBoostCents: 0,
        createdAt: new Date(),
        isFromVisitor: false,
        visitorName: getRandomName(),
      }
      return { ...state, queue: sortQueue([...state.queue, newRequest]) }
    }

    case 'ADD_SIMULATED_UPVOTE': {
      if (state.queue.length === 0) return state
      const randomRequest = state.queue[Math.floor(Math.random() * state.queue.length)]
      const updatedQueue = state.queue.map(r =>
        r.id === randomRequest.id ? { ...r, upvotes: r.upvotes + 1 } : r
      )
      return { ...state, queue: sortQueue(updatedQueue) }
    }

    case 'ADD_SIMULATED_BOOST': {
      if (state.queue.length === 0) return state
      const randomReq = state.queue[Math.floor(Math.random() * state.queue.length)]
      const tipAmount = [200, 300, 500, 700, 1000][Math.floor(Math.random() * 5)]
      const updatedQ = state.queue.map(r =>
        r.id === randomReq.id
          ? { ...r, totalTipsCents: r.totalTipsCents + tipAmount }
          : r
      )
      return { ...state, queue: sortQueue(updatedQ) }
    }

    case 'ADD_SIMULATED_TIP': {
      const tipAmount = [100, 200, 300, 500][Math.floor(Math.random() * 4)]
      return { ...state, totalEarningsCents: state.totalEarningsCents + tipAmount }
    }

    case 'ADD_SIMULATED_REACTION': {
      const reactions: ReactionType[] = ['love', 'fire', 'applause', 'wow', 'heart_eyes']
      const type = reactions[Math.floor(Math.random() * reactions.length)]
      const reaction: DemoReaction = {
        id: generateId('reaction'),
        type,
        timestamp: new Date(),
      }
      return { ...state, recentReactions: [...state.recentReactions.slice(-49), reaction] }
    }

    case 'ADD_SIMULATED_FEEDBACK': {
      const types: FeedbackType[] = ['amazing_performance', 'love_the_energy', 'great_song_choice', 'you_rock']
      const feedbackType = types[Math.floor(Math.random() * types.length)]
      const fb: DemoFeedback = {
        id: generateId('feedback'),
        type: feedbackType,
        timestamp: new Date(),
      }
      return { ...state, recentFeedback: [...state.recentFeedback.slice(-19), fb] }
    }

    case 'REGENERATE_REACTION_TOKEN': {
      const max = state.visitorHasTipped ? 10 : 3
      return { ...state, visitorReactionTokens: Math.min(state.visitorReactionTokens + 1, max) }
    }

    default:
      return state
  }
}
